import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Icon,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@elit-hair/elit-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbDotsVertical, TbPencil, TbPlus, TbTrash } from "react-icons/tb";

import { getIconByName } from "@/components/selectors/icons";

import { CustomLinkDialog } from "./custom-link-dialog";
import { type CustomLink, useCustomLinks } from "./use-custom-links";

export function CustomLinksPanel() {
    const { t } = useTranslation();
    const { state, isMobile } = useSidebar();
    const collapsed = state === "collapsed" && !isMobile;

    const { links, addLink, updateLink, removeLink } = useCustomLinks();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLink, setEditingLink] = useState<CustomLink | null>(null);

    const handleAddClick = useCallback(() => {
        setEditingLink(null);
        setDialogOpen(true);
    }, []);

    const handleEditClick = useCallback((link: CustomLink) => {
        setEditingLink(link);
        setDialogOpen(true);
    }, []);

    const handleDialogSave = useCallback(
        (data: { label: string; url: string; iconName: string }) => {
            if (editingLink) {
                updateLink(editingLink.id, data);
            } else {
                addLink(data);
            }
            setDialogOpen(false);
            setEditingLink(null);
        },
        [editingLink, addLink, updateLink]
    );

    const handleDeleteClick = useCallback(
        (link: CustomLink) => {
            removeLink(link.id);
        },
        [removeLink]
    );

    return (
        <>
            <SidebarMenu>
                {links.map((link) => {
                    const IconComponent = getIconByName(link.iconName);
                    return (
                        <SidebarMenuItem key={link.id}>
                            <SidebarMenuButton
                                asChild
                                tooltip={collapsed ? link.label : undefined}
                                className="group/link gap-1"
                            >
                                <div className="flex w-full min-w-0 items-center gap-1">
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex min-w-0 flex-1 items-center gap-2"
                                    >
                                        {IconComponent ? (
                                            <Icon icon={IconComponent} className="shrink-0 text-current" />
                                        ) : null}
                                        <span className="truncate">{link.label}</span>
                                    </a>
                                    {!collapsed && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover/link:opacity-100 data-[state=open]:opacity-100"
                                                    aria-label={t("custom_link_actions")}
                                                >
                                                    <Icon icon={TbDotsVertical} />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleEditClick(link)}>
                                                    <Icon icon={TbPencil} className="mr-2" />
                                                    {t("edit")}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => handleDeleteClick(link)}
                                                    className="text-destructive focus:text-destructive"
                                                >
                                                    <Icon icon={TbTrash} className="mr-2" />
                                                    {t("delete")}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    );
                })}
                <SidebarMenuItem>
                    <SidebarMenuButton onClick={handleAddClick} tooltip={collapsed ? t("custom_link_add") : undefined}>
                        <Icon icon={TbPlus} size={20} className="shrink-0" />
                        <span className="group-data-[collapsible=icon]:hidden text-muted-foreground">
                            {t("custom_link_add")}
                        </span>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>

            <CustomLinkDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editingLink={editingLink}
                onSave={handleDialogSave}
            />
        </>
    );
}
